<template>
	<div :class="componentClasses">
		<div class="segmented-control__group-label">
			<Label
				class="segmented-control__label"
				:labelText="labelText"
				:forElementId="uid"
				v-if="labelText"
			>
				<template v-slot:leading>
					<slot name="beforeLabel"> </slot>
				</template>

				<template v-slot:trailing>
					<slot name="afterLabel"> </slot>
				</template>
			</Label>
			<span v-if="$slots.labelIcon !== undefined">
				<slot name="labelIcon"></slot>
			</span>
		</div>

		<Divider
			class="segmented-control__divider"
			variant="dashed"
			v-if="hasDivider"
		/>

		<div class="segmented-control__group-segmented">
			<ul
				class="segmented-control__radio-list"
				:class="disabled ? 'is-disabled' : ''"
				ref="list"
			>
				<li
					:class="[
						'segmented-control__radio',
						disabledSomeButton(option)
					]"
					:style="segmentStyle"
					v-for="(option, index) in options"
					:key="`${uid}_${index}`"
					ref="segment"
				>
					<input
						type="radio"
						class="segmented-control__input"
						:name="`${uid}_${index}`"
						:value="option.value"
						:id="`${uid}_${index}`"
						v-model="valueSeleted"
						@change="change"
					/>
					<label
						class="segmented-control__input-label"
						:for="`${uid}_${index}`"
						tabindex="0"
						@keydown.space="selectFocusedOption(option)"
					>
						<span class="segmented-control__label-text">
							{{ option.text }}
						</span>
						<span
							class="segmented-control__icon"
							v-if="option.icon"
						>
							<i :class="['icon', 'icon-' + option.icon]" />
						</span>
					</label>
				</li>
			</ul>
			<span v-if="$slots.trailingIcon !== undefined">
				<slot name="trailingIcon"></slot>
			</span>
		</div>
		<HelpFeedback
			class="segmented-control__help-feedback"
			:helpText="helpText"
			v-if="helpText"
		/>
	</div>
</template>

<script>
import Label from '@/components/forms/shared-stateless-elements/label.vue';
import HelpFeedback from '@/components/forms/shared-stateless-elements/help-feedback.vue';
import baseComponentMixin from '@/mixins/base-component-mixin';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import variantClasses from './variant-classes/segmented-control-variant-classes';
import formComponentMixin from '@/mixins/form-component-mixin';
import Divider from '@/components/visual/divider/divider.vue';

export default {
	name: 'SegmentedControl',
	inheritAttrs: false,
	components: {
		Label,
		HelpFeedback,
		Divider
	},
	mixins: [baseComponentMixin, componentVariantMixin, formComponentMixin],
	created() {
		this.selectedValue = this.value;
		this.variantClasses = variantClasses;
	},
	mounted() {
		const segments = this.$refs.segment || [];
		const maxWidth = segments.reduce((previous, current) => {
			const clientRect = current.getBoundingClientRect();
			return Math.max(previous, clientRect.width);
		}, 0);

		this.segmentsMinWidth = `${maxWidth}px`;
	},
	data() {
		return {
			selectedValue: '',
			segmentsMinWidth: 'auto'
		};
	},
	props: {
		value: {
			type: [Number, String, Boolean],
			default: ''
		},
		/**
		 * Array of objects in format { text: 'text', value: 'value' }
		 */
		options: {
			type: Array,
			default: () => []
		},
		iconBeforeLabel: {
			type: Boolean,
			default: false
		},
		labelText: {
			type: String,
			default: null
		},
		helpText: {
			type: String,
			default: null
		},
		hasDivider: {
			type: Boolean,
			default: false
		},
		autoWidth: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		componentClasses() {
			return [
				'segmented-control',
				...this.variantClassList,
				this.hasError && 'has-error',
				this.iconBeforeLabel && 'has-icon-before'
			];
		},
		segmentStyle() {
			return {
				minWidth: this.autoWidth ? 'auto' : this.segmentsMinWidth
			};
		},
		valueSeleted: {
			get: function() {
				return this.selectedValue;
			},
			set: function(newValue) {
				this.selectedValue = newValue;
			}
		}
	},
	watch: {
		value: {
			immediate: true,
			handler(val) {
				this.selectedValue = val;
			}
		}
	},
	methods: {
		selectFocusedOption(option) {
			this.selectedValue = option.value;
		},
		disabledSomeButton(option) {
			return option.disabled && 'is-disabled';
		},
		change() {
			this.$emit('input', this.selectedValue);
			this.$emit('change', this.selectedValue);
		}
	}
};
</script>

<style lang="scss" scoped>
.segmented-control {
	display: inline-block;

	@mixin afterStyle {
		content: '';
		width: calc(100% + 2px);
		height: calc(100% + 2px);
		@include transform-vertical-center;
		left: 0;
		border: $focus-width $focus-style var(--brand-2);
		background: rgba(var(--brand-2_rgb), 0.1);
		z-index: $z-level1-1;
	}

	&__group-label {
		display: flex;
		align-items: center;
		gap: $gutter-md;
		margin-bottom: $gutter-sm;
		z-index: $z-level1-1;
		// Prevent shrinking when divider is present
		flex-shrink: 0;
		// Prevent shrinking when autoWidth is true
		// Allow to grow only to fit content
		&,
		&[style*='auto'] {
			flex: 0 0 auto;
			min-width: 0;
			width: auto;
		}
	}

	&__group-segmented {
		display: flex;
		align-items: center;
		gap: $gutter-md;
	}

	&__radio-list {
		flex-wrap: nowrap;
		display: flex;
	}

	&__radio {
		display: inline-flex;
		align-items: center;
		font-size: $txt-font-size;
		white-space: nowrap;

		&:not(:first-child) .segmented-control__input-label {
			border-left: none;
		}

		&:first-child .segmented-control__input-label,
		&:first-child .segmented-control__input-label::after {
			border-top-left-radius: $border-radius;
			border-bottom-left-radius: $border-radius;
		}

		&:last-child .segmented-control__input-label,
		&:last-child .segmented-control__input-label::after {
			border-top-right-radius: $border-radius;
			border-bottom-right-radius: $border-radius;
		}

		&:focus-visible,
		&:focus {
			outline: none;
		}
	}

	&__input {
		position: absolute;
		display: none;

		&:focus {
			outline: none;
		}

		&:checked + .segmented-control__input-label {
			position: relative;
			border-color: transparent;

			&::after {
				@include afterStyle;
			}
		}
	}

	&__icon {
		padding-left: $padding-sm;
		pointer-events: none;
		display: inline-flex;

		.icon {
			transform: scale(1.1);
			line-height: calc(
				#{$txt-font-size} * 1.15
			); // keep height of variants the same by using line-height value set for html in the normalize file, which is what the label text is using
		}
	}

	&__input-label {
		position: relative;
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		min-width: 66px;
		text-align: center;
		padding: $padding-xs $padding-md;
		background: var(--bg-0);
		color: var(--bg-0-fg-4);
		user-select: none;
		border: $field-border-width $focus-style var(--neutral-2);
		cursor: pointer;

		&:focus-visible,
		&:active {
			outline: none;
		}

		&:focus,
		&:hover {
			outline: none;

			&::after {
				@include afterStyle;
			}
		}
	}

	&.has-icon-before {
		.segmented-control {
			&__input-label {
				flex-direction: row-reverse;
			}

			&__icon {
				padding-left: 0;
				padding-right: $padding-sm;
			}
		}
	}

	&--only-icon {
		.segmented-control {
			&__icon {
				padding: 0;
				.icon {
					transform: scale(1.2);
				}
			}

			&__label-text {
				display: none;
			}
		}

		&.has-icon-before {
			.segmented-control__icon {
				padding: 0;
			}
		}
	}
}

@include left-label('segmented-control');
</style>
